"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDropPollOption } from "@/generated/models/ApiDropPollOption";
import { usePrefetchDropPollOptionVoters } from "@/hooks/useDropPollOptionVoters";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import type { CSSProperties, MouseEvent } from "react";
import { getVoteCountLabel } from "./WaveDropPoll.helpers";
import { PollOptionVoterPreviews } from "./PollOptionVoters";
interface PollResultOptionProps {
  readonly dropId: string;
  readonly option: ApiDropPollOption;
  readonly totalVotes: number;
  readonly isSelected: boolean;
  readonly isDimmed: boolean;
  readonly isExpanded: boolean;
  readonly showSelectionIndicator: boolean;
  readonly onToggle: (optionNo: number) => void;
}
const getSelectionClassName = ({
  canShowVoters,
  isSelected
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
}): string => {
  if (isSelected) {
    return "tw-border-iron-600 tw-bg-iron-800 tw-shadow-[0_0_15px_rgba(245,245,245,0.04)]";
  }
  if (canShowVoters) {
    return "tw-border-iron-800 tw-bg-iron-800/60 desktop-hover:hover:tw-border-iron-700 desktop-hover:hover:tw-bg-iron-800/80";
  }
  return "tw-border-iron-800 tw-bg-iron-800/60";
};
const getPollResultOptionClassName = ({
  canShowVoters,
  isSelected,
  isDimmed
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
  readonly isDimmed: boolean;
}): string => {
  const selectionClassName = getSelectionClassName({
    canShowVoters,
    isSelected
  });
  const opacityClassName = isDimmed ? "tw-opacity-80 desktop-hover:hover:tw-opacity-100" : "tw-opacity-100";
  const interactionClassName = canShowVoters ? "tw-cursor-pointer focus-visible:tw-ring-2 focus-visible:tw-ring-white/30" : "tw-cursor-default";
  return `tw-group/result tw-relative tw-flex tw-min-h-11 tw-w-full tw-transform-gpu tw-items-stretch tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-p-0 tw-text-left tw-outline-none tw-transition-all tw-duration-300 ${interactionClassName} ${selectionClassName} ${opacityClassName}`;
};
function PollResultSelectedMarker() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="tw-mt-0.5 tw-flex tw-size-4 tw-shrink-0 tw-scale-110 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-50 tw-bg-iron-50 tw-transition-all tw-duration-300 desktop-hover:group-hover/result:tw-scale-100 desktop-hover:group-hover/result:tw-border-iron-200 desktop-hover:group-hover/result:tw-bg-iron-200" aria-hidden="true"><CheckIcon className="tw-size-2.5 tw-text-iron-950 tw-transition-all tw-duration-300" strokeWidth={3} /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const getPollResultLabelClassName = ({
  canShowVoters,
  isSelected
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
}): string => {
  if (isSelected) {
    return "tw-font-medium tw-text-iron-50";
  }
  if (canShowVoters) {
    return "tw-font-medium tw-text-iron-100 desktop-hover:group-hover/result:tw-text-iron-50";
  }
  return "tw-font-medium tw-text-iron-100";
};
function PollResultOptionLabel(t0) {
  const $ = _c(6);
  const {
    canShowVoters,
    isSelected,
    optionString
  } = t0;
  let t1;
  if ($[0] !== canShowVoters || $[1] !== isSelected) {
    t1 = getPollResultLabelClassName({
      canShowVoters,
      isSelected
    });
    $[0] = canShowVoters;
    $[1] = isSelected;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const labelClassName = t1;
  const t2 = `tw-min-w-0 tw-break-words tw-text-[13px] tw-leading-5 tw-transition-colors tw-duration-300 ${labelClassName}`;
  let t3;
  if ($[3] !== optionString || $[4] !== t2) {
    t3 = <span className={t2}>{optionString}</span>;
    $[3] = optionString;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
const getPollResultVoteCountClassName = ({
  canShowVoters,
  isSelected
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
}): string => {
  if (isSelected) {
    return "tw-text-iron-200";
  }
  if (canShowVoters) {
    return "tw-text-iron-300 desktop-hover:group-hover/result:tw-text-iron-200";
  }
  return "tw-text-iron-300";
};
const getPollResultPercentageClassName = ({
  canShowVoters,
  isSelected
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
}): string => {
  if (isSelected) {
    return "tw-text-iron-100";
  }
  if (canShowVoters) {
    return "tw-text-iron-200 desktop-hover:group-hover/result:tw-text-iron-100";
  }
  return "tw-text-iron-200";
};
function PollResultOptionStats(t0) {
  const $ = _c(28);
  const {
    canShowVoters,
    dropId,
    isExpanded,
    isSelected,
    option,
    percentage
  } = t0;
  let t1;
  if ($[0] !== canShowVoters || $[1] !== isSelected) {
    t1 = getPollResultVoteCountClassName({
      canShowVoters,
      isSelected
    });
    $[0] = canShowVoters;
    $[1] = isSelected;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const voteCountClassName = t1;
  let t2;
  if ($[3] !== canShowVoters || $[4] !== isSelected) {
    t2 = getPollResultPercentageClassName({
      canShowVoters,
      isSelected
    });
    $[3] = canShowVoters;
    $[4] = isSelected;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const percentageClassName = t2;
  const chevronClassName = isExpanded ? "tw-rotate-180" : "";
  let t3;
  if ($[6] !== canShowVoters || $[7] !== dropId || $[8] !== option) {
    t3 = canShowVoters && <PollOptionVoterPreviews dropId={dropId} option={option} />;
    $[6] = canShowVoters;
    $[7] = dropId;
    $[8] = option;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = `tw-text-[11.5px] tw-font-medium tw-transition-colors ${voteCountClassName}`;
  let t5;
  if ($[10] !== option.votes) {
    t5 = getVoteCountLabel(option.votes);
    $[10] = option.votes;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t6) {
    t7 = <span className="tw-flex tw-h-5 tw-items-center tw-gap-1.5">{t3}{t6}</span>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = `tw-w-8 tw-text-right tw-text-xs tw-font-bold tw-transition-colors ${percentageClassName}`;
  let t9;
  if ($[18] !== percentage || $[19] !== t8) {
    t9 = <span className={t8}>{percentage}%</span>;
    $[18] = percentage;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== canShowVoters || $[22] !== chevronClassName) {
    t10 = canShowVoters && <ChevronDownIcon className={`tw-size-3.5 tw-flex-shrink-0 tw-text-iron-600 tw-transition-transform tw-duration-200 desktop-hover:group-hover/result:tw-text-iron-400 ${chevronClassName}`} aria-hidden="true" />;
    $[21] = canShowVoters;
    $[22] = chevronClassName;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7 || $[26] !== t9) {
    t11 = <div className="tw-ml-2 tw-flex tw-h-5 tw-flex-shrink-0 tw-translate-x-0 tw-animate-poll-result-stats-in tw-items-center tw-gap-2.5 tw-opacity-100 tw-transition-all tw-duration-500 motion-reduce:tw-animate-none">{t7}{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
const getPollResultFillClassName = ({
  canShowVoters,
  isSelected
}: {
  readonly canShowVoters: boolean;
  readonly isSelected: boolean;
}): string => {
  if (isSelected) {
    return "tw-bg-iron-700";
  }
  if (canShowVoters) {
    return "tw-bg-iron-800 desktop-hover:group-hover/result:tw-bg-iron-700";
  }
  return "tw-bg-iron-800";
};
export function PollResultOption(t0) {
  const $ = _c(59);
  const {
    dropId,
    option,
    totalVotes,
    isSelected,
    isDimmed,
    isExpanded,
    showSelectionIndicator,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== option.votes || $[1] !== totalVotes) {
    t1 = totalVotes > 0 ? Math.round(option.votes / totalVotes * 100) : 0;
    $[0] = option.votes;
    $[1] = totalVotes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const percentage = t1;
  const fillScale = Math.max(0, Math.min(100, percentage)) / 100;
  const prefetchVoters = usePrefetchDropPollOptionVoters();
  const canShowVoters = option.votes > 0;
  let t2;
  if ($[3] !== canShowVoters || $[4] !== dropId || $[5] !== option.option_no || $[6] !== prefetchVoters) {
    t2 = () => prefetchVoters({
      dropId,
      optionNo: option.option_no,
      enabled: canShowVoters
    });
    $[3] = canShowVoters;
    $[4] = dropId;
    $[5] = option.option_no;
    $[6] = prefetchVoters;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handlePrefetchVoters = t2;
  let t3;
  if ($[8] !== onToggle || $[9] !== option.option_no) {
    t3 = event => {
      event.stopPropagation();
      onToggle(option.option_no);
    };
    $[8] = onToggle;
    $[9] = option.option_no;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleToggle = t3;
  let t4;
  if ($[11] !== canShowVoters || $[12] !== isDimmed || $[13] !== isSelected) {
    t4 = getPollResultOptionClassName({
      canShowVoters,
      isSelected,
      isDimmed
    });
    $[11] = canShowVoters;
    $[12] = isDimmed;
    $[13] = isSelected;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const optionClassName = t4;
  let t5;
  if ($[15] !== canShowVoters || $[16] !== isSelected) {
    t5 = getPollResultFillClassName({
      canShowVoters,
      isSelected
    });
    $[15] = canShowVoters;
    $[16] = isSelected;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const fillClassName = t5;
  let t6;
  if ($[18] !== fillScale) {
    t6 = fillScale.toString();
    $[18] = fillScale;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = {
      "--poll-result-fill-scale": t6
    };
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const fillStyle = t7 as CSSProperties;
  const selectedAriaLabel = isSelected ? " Your vote." : "";
  const t8 = `tw-absolute tw-inset-y-0 tw-left-0 tw-w-full tw-origin-left tw-scale-x-[var(--poll-result-fill-scale)] tw-transform-gpu tw-animate-poll-result-fill tw-transition-[background-color] tw-duration-700 tw-ease-out motion-reduce:tw-animate-none ${fillClassName}`;
  let t9;
  if ($[22] !== fillStyle || $[23] !== t8) {
    t9 = <div className={t8} style={fillStyle} aria-hidden="true" />;
    $[22] = fillStyle;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isSelected || $[26] !== showSelectionIndicator) {
    t10 = showSelectionIndicator && isSelected && <PollResultSelectedMarker />;
    $[25] = isSelected;
    $[26] = showSelectionIndicator;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== canShowVoters || $[29] !== isSelected || $[30] !== option.option_string) {
    t11 = <PollResultOptionLabel canShowVoters={canShowVoters} isSelected={isSelected} optionString={option.option_string} />;
    $[28] = canShowVoters;
    $[29] = isSelected;
    $[30] = option.option_string;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11) {
    t12 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2.5">{t10}{t11}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== canShowVoters || $[36] !== dropId || $[37] !== isExpanded || $[38] !== isSelected || $[39] !== option || $[40] !== percentage) {
    t13 = <PollResultOptionStats canShowVoters={canShowVoters} dropId={dropId} isExpanded={isExpanded} isSelected={isSelected} option={option} percentage={percentage} />;
    $[35] = canShowVoters;
    $[36] = dropId;
    $[37] = isExpanded;
    $[38] = isSelected;
    $[39] = option;
    $[40] = percentage;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13) {
    t14 = <div className="tw-relative tw-flex tw-w-full tw-min-w-0 tw-items-start tw-justify-between tw-gap-3 tw-px-3.5 tw-py-3">{t12}{t13}</div>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t14 || $[46] !== t9) {
    t15 = <>{t9}{t14}</>;
    $[45] = t14;
    $[46] = t9;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const optionContent = t15;
  let t16;
  if ($[48] !== canShowVoters || $[49] !== handlePrefetchVoters || $[50] !== handleToggle || $[51] !== isExpanded || $[52] !== option.option_string || $[53] !== option.votes || $[54] !== optionClassName || $[55] !== optionContent || $[56] !== percentage || $[57] !== selectedAriaLabel) {
    t16 = <div className="tw-overflow-hidden tw-rounded-lg">{canShowVoters ? <button type="button" aria-expanded={isExpanded} aria-label={`${isExpanded ? "Hide" : "View"} voters for ${option.option_string}. ${getVoteCountLabel(option.votes)}, ${percentage} percent.${selectedAriaLabel}`} onClick={handleToggle} onFocus={handlePrefetchVoters} onPointerEnter={handlePrefetchVoters} className={optionClassName}>{optionContent}</button> : <div className={optionClassName}>{optionContent}</div>}</div>;
    $[48] = canShowVoters;
    $[49] = handlePrefetchVoters;
    $[50] = handleToggle;
    $[51] = isExpanded;
    $[52] = option.option_string;
    $[53] = option.votes;
    $[54] = optionClassName;
    $[55] = optionContent;
    $[56] = percentage;
    $[57] = selectedAriaLabel;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  return t16;
}